import { CloseOutlined, PhotoCameraOutlined, UploadFileOutlined } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { ChangeEvent, JSX, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toDateInputValue } from '../../../library/dateTimeHelper';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import {
    ACCEPTED_DOCUMENT_TYPES,
    CAMERA_CAPTURE_TYPE,
    categoryLabelKey,
    DOCUMENT_TITLE_MAX,
    formatFileSize,
    IMedicalDocument,
    isAcceptedDocumentFile,
    MAX_DOCUMENT_BYTES,
    MAX_DOCUMENTS_PER_UPLOAD,
    MAX_UPLOAD_TOTAL_BYTES,
    MEDICAL_DOCUMENT_CATEGORIES,
    MedicalDocumentCategory,
    photoFilename,
    titleFromFilename,
} from '../../../library/models/medicalRecord';
import { useApiFormError } from '../../../library/useApiFormError';
import { useIsCompact } from '../../../library/useIsCompact';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import { AppDatePicker } from '../../pickers/AppDatePicker';
import {
    blockedHintStyles,
    descriptionStyles,
    dialogActionsStyles,
    dialogFormStyles,
    dialogTitleStyles,
    fileListStyles,
    fileNameStyles,
    fileRowStyles,
    formAlertStyles,
    pickHintStyles,
    pickRowStyles,
    progressStyles,
    sharedFieldsStyles,
    takePhotoButtonStyles,
} from './uploadDocumentsDialog.styles';
import { createUploadDocumentsSchema, UploadDocumentsFormValues } from './uploadDocumentsSchema';

interface UploadDocumentsDialogProps {
    open: boolean;
    patientId: number;
    onClose: () => void;
    /** The patient's record has to pick the new lines up. */
    onUploaded: () => void;
}

const megabytes = (bytes: number): number => bytes / (1024 * 1024);

/** The server fields this form can place a message on; anything else goes to the alert. */
const SERVER_FIELDS: string[] = ['category', 'issuedOn'];

/**
 * Adds documents a patient brought in -- a scan, a set of results, a letter -- to their
 * record. Several at once, since that is how they arrive: three photographed pages of one
 * report are three files.
 *
 * Five things about this dialog are deliberate.
 *
 * **A file is turned away the moment it is chosen**, not after it has been uploaded. The
 * limits here are the API's own, and the API re-checks every one of them; restating them is
 * what saves somebody on a clinic's uplink waiting for twenty megabytes to be told no.
 *
 * **Nothing is shrunk.** The receipt photograph in `EmitInvoiceDialog` is downscaled because
 * a till receipt is a strip of thermal paper; the small print of a radiology report is the
 * point of having it, so what was chosen is what is stored.
 *
 * **A page can be photographed where it is handed over.** `capture="environment"` opens the
 * rear camera straight from a phone or a tablet, one shot at a time, each added to what is
 * already there -- and through the same checks as a chosen file. A phone calls every capture
 * the same thing, so a photograph is renamed for the moment it was taken and titled by its
 * number rather than left as three rows called "image". The button is not drawn for a mouse,
 * where `capture` is ignored and it would only be a second file picker.
 *
 * **The date asked for is the date on the document**, defaulting to today only because that
 * is the commonest answer for something printed this morning. It is what the record is
 * ordered by, so an old scan belongs where it was taken.
 *
 * **All or nothing.** The API stores every file or none, so there is no partial state to
 * draw: the dialog either closes on success or stays as it was, with the reason.
 *
 * The files live outside react-hook-form, beside it and in step with `documents` by
 * position -- the same split `EmitInvoiceDialog` makes for its photograph, for the same
 * reason.
 */
export const UploadDocumentsDialog = (props: UploadDocumentsDialogProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const isCompact = useIsCompact();

    const schema = useMemo(() => createUploadDocumentsSchema(translate), [translate]);

    const defaultValues = useMemo(
        (): UploadDocumentsFormValues => ({
            category: '',
            issuedOn: toDateInputValue(new Date()),
            documents: [],
        }),
        []
    );

    const {
        register,
        control,
        handleSubmit,
        reset,
        setError,
        formState: { errors, isValid, isSubmitting },
    } = useValidatedForm<UploadDocumentsFormValues>(schema, { defaultValues });

    const { fields, append, remove } = useFieldArray<UploadDocumentsFormValues, 'documents', 'key'>({
        control,
        name: 'documents',
        keyName: 'key',
    });

    const { formError, report, clear } = useApiFormError<UploadDocumentsFormValues>(
        setError, SERVER_FIELDS, translate, ResourcesIds.UPLOAD_DOCUMENTS_ERROR);

    /** In step with `fields` by position: file `i` is filed under title `i`. */
    const [files, setFiles] = useState<File[]>([]);
    /** Why some of what was just chosen was not taken. Replaced on every pick. */
    const [refused, setRefused] = useState<string[]>([]);
    const [progress, setProgress] = useState<number>(0);

    const fileInput = useRef<HTMLInputElement>(null);
    const cameraInput = useRef<HTMLInputElement>(null);
    /** How many photographs this opening of the dialog has taken. Never counted back down. */
    const photoCount = useRef<number>(0);

    const upload = useReactMutation(services.medicalRecords.uploadDocumentsMutation());

    // Cleared on every open, so the last patient's files are never offered for the next one.
    useEffect(() => {
        if (props.open) {
            reset(defaultValues);
            clear();
            setFiles([]);
            setRefused([]);
            setProgress(0);
            photoCount.current = 0;
        }
    }, [props.open, reset, defaultValues, clear]);

    /**
     * Takes what can be taken and says why the rest was not. One bad file among five does
     * not cost the other four: they are added, and the fifth is named.
     */
    const addFiles = (chosen: File[], titleFor: (file: File) => string): void => {
        const accepted: File[] = [];
        const reasons: string[] = [];
        let total: number = files.reduce((sum: number, file: File) => sum + file.size, 0);

        chosen.forEach((file: File) => {
            if (!isAcceptedDocumentFile(file)) {
                reasons.push(translate(ResourcesIds.UPLOAD_DOCUMENTS_FILE_TYPE_UNSUPPORTED, { name: file.name }));
            } else if (file.size > MAX_DOCUMENT_BYTES) {
                reasons.push(translate(ResourcesIds.UPLOAD_DOCUMENTS_FILE_TOO_LARGE, {
                    name: file.name,
                    maxMb: megabytes(MAX_DOCUMENT_BYTES),
                }));
            } else if (files.length + accepted.length >= MAX_DOCUMENTS_PER_UPLOAD) {
                const tooMany: string = translate(ResourcesIds.UPLOAD_DOCUMENTS_TOO_MANY, {
                    max: MAX_DOCUMENTS_PER_UPLOAD,
                });

                // Said once, however many were over.
                if (!reasons.includes(tooMany)) {
                    reasons.push(tooMany);
                }
            } else if (total + file.size > MAX_UPLOAD_TOTAL_BYTES) {
                const tooMuch: string = translate(ResourcesIds.UPLOAD_DOCUMENTS_TOTAL_TOO_LARGE, {
                    maxMb: megabytes(MAX_UPLOAD_TOTAL_BYTES),
                });

                if (!reasons.includes(tooMuch)) {
                    reasons.push(tooMuch);
                }
            } else {
                accepted.push(file);
                total += file.size;
            }
        });

        setRefused(reasons);

        if (accepted.length > 0) {
            setFiles((previous: File[]) => [...previous, ...accepted]);
            append(accepted.map((file: File) => ({ title: titleFor(file) })));
        }
    };

    const onPickFiles = (event: ChangeEvent<HTMLInputElement>): void => {
        const chosen: File[] = Array.from(event.target.files ?? []);

        // Cleared straight away so choosing the same file twice still fires a change event.
        event.target.value = '';

        addFiles(chosen, (file: File) => titleFromFilename(file.name));
    };

    const onTakePhoto = (event: ChangeEvent<HTMLInputElement>): void => {
        const taken: File[] = Array.from(event.target.files ?? []);

        event.target.value = '';

        // The same bytes under a name of their own; nothing is decoded or re-encoded.
        const photos: File[] = taken.map((file: File) =>
            new File([file], photoFilename(new Date(), file.type), {
                type: file.type,
                lastModified: file.lastModified,
            }));

        // Numbered as they are accepted, so a photograph that was turned away takes no number
        // and one that was taken back out does not hand its number to the next.
        addFiles(photos, () => {
            photoCount.current += 1;

            return translate(ResourcesIds.UPLOAD_DOCUMENTS_PHOTO_TITLE, { number: photoCount.current });
        });
    };

    const onRemove = (index: number): void => {
        setFiles((previous: File[]) => previous.filter((_file: File, at: number) => at !== index));
        remove(index);
    };

    const onValid = (values: UploadDocumentsFormValues): void => {
        clear();
        setProgress(0);

        upload.mutate(
            {
                request: {
                    patientId: props.patientId,
                    category: values.category as MedicalDocumentCategory,
                    issuedOn: values.issuedOn,
                    titles: values.documents.map((document: { title: string }) => document.title.trim()),
                },
                files,
                onProgress: setProgress,
            },
            {
                onSuccess: (stored: IMedicalDocument[]) => {
                    showSnackbar(
                        translate(ResourcesIds.UPLOAD_DOCUMENTS_SUCCESS, { count: stored.length }),
                        'success'
                    );
                    props.onUploaded();
                    props.onClose();
                },
                // The API's own words: it names the file it refused and why.
                onError: report,
            }
        );
    };

    const isBusy: boolean = isSubmitting || upload.isPending;
    const hasFiles: boolean = files.length > 0;

    return (
        <Dialog
            open={props.open}
            // Not dismissed by a stray click while bytes are leaving: the request would go on
            // and its result would arrive with nowhere to be shown.
            onClose={isBusy ? undefined : props.onClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isCompact}
        >
            <DialogTitle sx={dialogTitleStyles}>
                {translate(ResourcesIds.UPLOAD_DOCUMENTS_TITLE)}
            </DialogTitle>

            <Box component="form" onSubmit={handleSubmit(onValid)} noValidate sx={dialogFormStyles}>
                <DialogContent>
                    <Typography variant="body2" sx={descriptionStyles}>
                        {translate(ResourcesIds.UPLOAD_DOCUMENTS_DESCRIPTION, {
                            maxMb: megabytes(MAX_DOCUMENT_BYTES),
                        })}
                    </Typography>

                    {formError && (
                        <Alert severity="error" sx={formAlertStyles}>
                            {formError}
                        </Alert>
                    )}

                    <Box sx={sharedFieldsStyles}>
                        <Controller
                            name="category"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    select
                                    fullWidth
                                    required
                                    label={translate(ResourcesIds.UPLOAD_DOCUMENTS_CATEGORY_LABEL)}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    name={field.name}
                                    inputRef={field.ref}
                                    disabled={isBusy}
                                    {...fieldProps(errors, 'category')}
                                >
                                    {MEDICAL_DOCUMENT_CATEGORIES.map((category: MedicalDocumentCategory) => (
                                        <MenuItem key={category} value={category}>
                                            {translate(categoryLabelKey(category))}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        />

                        <Controller
                            name="issuedOn"
                            control={control}
                            render={({ field }) => (
                                <AppDatePicker
                                    label={translate(ResourcesIds.UPLOAD_DOCUMENTS_ISSUED_ON_LABEL)}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    name={field.name}
                                    inputRef={field.ref}
                                    fullWidth
                                    required
                                    disableFuture
                                    disabled={isBusy}
                                    {...fieldProps(errors, 'issuedOn',
                                        translate(ResourcesIds.UPLOAD_DOCUMENTS_ISSUED_ON_HINT))}
                                />
                            )}
                        />
                    </Box>

                    {hasFiles && (
                        <Box sx={fileListStyles}>
                            {fields.map((field, index: number) => (
                                <Box key={field.key} sx={fileRowStyles}>
                                    <Typography variant="caption" sx={fileNameStyles}>
                                        {files[index]?.name}
                                        {files[index] ? ` · ${formatFileSize(files[index].size)}` : ''}
                                    </Typography>

                                    <TextField
                                        {...register(`documents.${index}.title` as const)}
                                        fullWidth
                                        required
                                        size="small"
                                        label={translate(ResourcesIds.UPLOAD_DOCUMENTS_TITLE_LABEL)}
                                        disabled={isBusy}
                                        slotProps={{ htmlInput: { maxLength: DOCUMENT_TITLE_MAX } }}
                                        error={!!errors.documents?.[index]?.title}
                                        helperText={errors.documents?.[index]?.title?.message}
                                    />

                                    <IconButton
                                        aria-label={translate(ResourcesIds.UPLOAD_DOCUMENTS_REMOVE_FILE, {
                                            name: files[index]?.name ?? '',
                                        })}
                                        onClick={() => onRemove(index)}
                                        disabled={isBusy}
                                    >
                                        <CloseOutlined />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {refused.length > 0 && (
                        <Alert severity="warning" sx={progressStyles}>
                            {refused.map((reason: string) => (
                                <Box key={reason}>{reason}</Box>
                            ))}
                        </Alert>
                    )}

                    <Box sx={pickRowStyles}>
                        <Button
                            variant="outlined"
                            startIcon={<UploadFileOutlined />}
                            onClick={() => fileInput.current?.click()}
                            disabled={isBusy || files.length >= MAX_DOCUMENTS_PER_UPLOAD}
                        >
                            {translate(hasFiles
                                ? ResourcesIds.UPLOAD_DOCUMENTS_ADD_MORE
                                : ResourcesIds.UPLOAD_DOCUMENTS_CHOOSE_FILES)}
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<PhotoCameraOutlined />}
                            onClick={() => cameraInput.current?.click()}
                            disabled={isBusy || files.length >= MAX_DOCUMENTS_PER_UPLOAD}
                            sx={takePhotoButtonStyles}
                        >
                            {translate(ResourcesIds.UPLOAD_DOCUMENTS_TAKE_PHOTO)}
                        </Button>

                        {!hasFiles && (
                            <Typography variant="caption" sx={pickHintStyles}>
                                {translate(ResourcesIds.UPLOAD_DOCUMENTS_NO_FILES)}
                            </Typography>
                        )}

                        {/* The three types by name rather than `image/*`: a HEIC or a GIF
                            would be offered, chosen, uploaded and then refused by the API.
                            Listing JPEG also makes iOS hand over a JPEG for a HEIC photo. */}
                        <input
                            ref={fileInput}
                            type="file"
                            multiple
                            accept={ACCEPTED_DOCUMENT_TYPES.join(',')}
                            hidden
                            onChange={onPickFiles}
                            data-testid="document-files-input"
                        />

                        {/* One shot at a time, so no `multiple`: with it a phone offers its
                            photo library instead of opening the camera. */}
                        <input
                            ref={cameraInput}
                            type="file"
                            accept={CAMERA_CAPTURE_TYPE}
                            capture="environment"
                            hidden
                            onChange={onTakePhoto}
                            data-testid="document-camera-input"
                        />
                    </Box>

                    {upload.isPending && (
                        <Box sx={progressStyles}>
                            <Typography variant="caption" sx={pickHintStyles}>
                                {translate(ResourcesIds.UPLOAD_DOCUMENTS_UPLOADING)}
                            </Typography>
                            <LinearProgress variant="determinate" value={Math.round(progress * 100)} />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={dialogActionsStyles}>
                    {/* Only once there is something to fix: before any file is chosen the
                        line above already says what is missing. */}
                    {hasFiles && !isValid && (
                        <Typography variant="caption" sx={blockedHintStyles}>
                            {translate(ResourcesIds.FORM_BLOCKED_SAVE)}
                        </Typography>
                    )}

                    <Button onClick={props.onClose} disabled={isBusy}>
                        {translate(ResourcesIds.CANCEL_TEXT)}
                    </Button>
                    <Button type="submit" variant="contained" disabled={!isValid || !hasFiles || isBusy}>
                        {translate(ResourcesIds.UPLOAD_DOCUMENTS_SUBMIT)}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
